/**
 * FFS Field Definitions
 *
 * Centralized lookup for FFS calculation field metadata including:
 * - Display labels (with subscript notation)
 * - Formulas (for calculated fields)
 * - Section categorization
 *
 * Labels and formulas are stored in a structured format for proper PDF rendering.
 * Use parseFormula() to convert to renderable parts.
 *
 * Syntax for labels/formulas:
 * - _x or _{xyz} for subscript
 * - ^x or ^{xyz} for superscript
 * - Regular text is rendered normally
 *
 * Examples:
 * - "t_{mm}" renders as t with subscript "mm"
 * - "MAWP^L" renders as MAWP with superscript "L"
 * - "t_{min}^c" renders as t with subscript "min" and superscript "c"
 */

export type FfsFieldSection =
  | 'user_params'
  | 'data_values'
  | 'mawp'
  | 'calculated_values' // Part 4
  | 'thickness_criteria' // Part 4
  | 'flaw_size_criteria' // Part 5
  | 'final_acceptability_criteria' // Part 5

export interface FfsFieldDefinition {
  /** Display label in formula syntax (e.g., "t_{mm}") */
  label: string
  /** Description of the field */
  description: string
  /** Formula string in formula syntax (null for direct measurements) */
  formula: string | null
  /** Which section this field belongs to */
  section: FfsFieldSection
}

/**
 * Context for selecting the correct formula variant by FFS Part/Level and optional indication type.
 */
export interface FfsFormulaContext {
  ffsPart: 'part-4' | 'part-5'
  ffsLevel: '1' | '2'
  indicationType?: string
}

/**
 * Optional overrides to specialize formulas by Part/Level/IndicationType.
 * Keep empty to fall back to base definitions. Populate as differences are specified.
 */
const FORMULA_OVERRIDES: {
  [part in 'part-4' | 'part-5']?: {
    [level in '1' | '2']?: {
      default?: Record<string, string | null>
      byIndicationType?: Record<string, Record<string, string | null>>
    }
  }
} = {
  // Populate with concrete overrides as needed.
  'part-5': {
    // Level 1 overrides
    '1': {
      default: {
        // Lambda in Part 5 is defined using inner diameter and t^c
        lambda: '1.285s / sqrt(D_{inner} * t^c)',
      },
    },
    // Level 2 overrides
    '2': {
      default: {
        lambda: '1.285s / sqrt(D_{inner} * t^c)',
      },
    },
  },
}

function getFormulaOverride(
  key: string,
  _section: FfsFieldSection | undefined,
  context: FfsFormulaContext | undefined
): string | null | undefined {
  if (!context) return undefined
  const partNode = FORMULA_OVERRIDES[context.ffsPart]
  if (!partNode) return undefined
  const levelNode = partNode[context.ffsLevel]
  if (!levelNode) return undefined

  // Try indication-type-specific override first
  if (context.indicationType && levelNode.byIndicationType) {
    const byType = levelNode.byIndicationType[context.indicationType]
    if (byType && Object.prototype.hasOwnProperty.call(byType, key)) {
      return byType[key]
    }
  }

  // Fall back to level defaults
  if (
    levelNode.default &&
    Object.prototype.hasOwnProperty.call(levelNode.default, key)
  ) {
    return levelNode.default[key]
  }

  return undefined
}

/**
 * Field definitions for all FFS calculation fields
 *
 * Labels use formula syntax:
 * - _x or _{xyz} for subscript
 * - ^x or ^{xyz} for superscript
 */
export const FFS_FIELD_DEFINITIONS: Record<string, FfsFieldDefinition> = {
  // ==================== User Parameters ====================
  fca: {
    label: 'FCA',
    description: 'Future Corrosion Allowance',
    formula: null,
    section: 'user_params',
  },
  fcaMl: {
    label: 'FCA_{ml}',
    description: 'Future Corrosion Allowance for Metal Loss',
    formula: null,
    section: 'user_params',
  },

  // ==================== Data Values ====================
  tmm: {
    label: 't_{mm}',
    description: 'Minimum measured thickness within corrosion area',
    formula: null,
    section: 'data_values',
  },
  trd: {
    label: 't_{rd}',
    description: 'Minimum measured thickness just outside of corrosion area',
    formula: null,
    section: 'data_values',
  },
  tc: {
    label: 't^c',
    description:
      'Future corroded wall thickness just outside of corrosion area',
    formula: 't_{rd} - FCA',
    section: 'data_values',
  },
  longitudinalFlawLength: {
    label: 's',
    description: 'Longitudinal length of metal loss region',
    formula: null,
    section: 'data_values',
  },
  latitudinalFlawLength: {
    label: 'c',
    description: 'Circumferential length of metal loss region',
    formula: null,
    section: 'data_values',
  },
  innerDiameter: {
    label: 'D_{inner}',
    description: 'Inner diameter',
    formula: null,
    section: 'data_values',
  },
  outerDiameter: {
    label: 'D_{outer}',
    description: 'Outer diameter',
    formula: null,
    section: 'data_values',
  },
  lambda: {
    label: 'lambda',
    description: 'Longitudinal flaw length parameter',
    formula: '1.285s / sqrt(D * t^c)',
    section: 'data_values',
  },
  nominal: {
    label: 't_{nom}',
    description: 'Nominal wall thickness',
    formula: null,
    section: 'data_values',
  },
  t_ml: {
    label: 't_{ml}',
    description: 'Thickness for metal loss calculations (Part 4)',
    formula: null,
    section: 'data_values',
  },
  d_ml: {
    label: 'D_{ml}',
    description: 'Diameter for metal loss calculations (Part 4)',
    formula: null,
    section: 'data_values',
  },

  // ==================== MAWP ====================
  e: {
    label: 'E',
    description: 'Quality/Efficiency factor',
    formula: null,
    section: 'mawp',
  },
  y: {
    label: 'Y',
    description: 'Temperature coefficient',
    formula: null,
    section: 'mawp',
  },
  w: {
    label: 'W',
    description: 'Weld joint strength reduction factor',
    formula: null,
    section: 'mawp',
  },
  s_y: {
    label: 'S_y',
    description: 'Yield strength at design temperature',
    formula: null,
    section: 'mawp',
  },
  tsl: {
    label: 't_{sl}',
    description: 'Supplemental thickness for longitudinal stress',
    formula: null,
    section: 'mawp',
  },
  mawp_c: {
    label: 'MAWP^C',
    description: 'MAWP based on circumferential stress',
    formula: '(2 * S * E * t) / (D - 2 * Y * t)',
    section: 'mawp',
  },
  mawp_l: {
    label: 'MAWP^L',
    description: 'MAWP based on longitudinal stress',
    formula: '(4 * S * E * t) / (D - 0.8 * t)',
    section: 'mawp',
  },
  mawp_design: {
    label: 'MAWP Design',
    description: 'Design MAWP',
    formula: null,
    section: 'mawp',
  },
  mawpr: {
    label: 'MAWP_r',
    description: 'Reduced MAWP accounting for metal loss',
    formula: 'MAWP * RSF',
    section: 'mawp',
  },

  // ==================== Part 4: Calculated Values ====================
  rt: {
    label: 'R_t',
    description: 'Remaining thickness ratio',
    formula: '(t_{mm} - FCA_{ml}) / t_{ml}',
    section: 'calculated_values',
  },
  rsf_a: {
    label: 'RSF_a',
    description: 'Acceptable remaining strength factor',
    formula: null,
    section: 'calculated_values',
  },
  q: {
    label: 'Q',
    description: 'Length factor from table lookup',
    formula: 'f(R_t, RSF_a)',
    section: 'calculated_values',
  },
  l: {
    label: 'L',
    description: 'Length for thickness averaging',
    formula: 'Q * sqrt(D_{ml} * t_{ml})',
    section: 'calculated_values',
  },
  ts_am: {
    label: 't_{am}^s',
    description: 'Average measured thickness in circumferential direction',
    formula: 'CTP average (s-direction)',
    section: 'calculated_values',
  },
  tc_am: {
    label: 't_{am}^c',
    description: 'Average measured thickness in longitudinal direction',
    formula: 'CTP average (c-direction)',
    section: 'calculated_values',
  },

  // ==================== Part 4: Thickness Criteria ====================
  t_lim: {
    label: 't_{lim}',
    description: 'Limiting thickness',
    formula: 'max(0.2 * t_{nom}, 2.5mm)',
    section: 'thickness_criteria',
  },
  t_c_min: {
    label: 't_{min}^C',
    description: 'Minimum circumferential thickness from design MAWP',
    formula: 'P * D / (2 * S * E + 2 * Y * P)',
    section: 'thickness_criteria',
  },
  t_l_min: {
    label: 't_{min}^L',
    description: 'Minimum longitudinal thickness from design MAWP',
    formula: 'P * D / (4 * S * E + 0.8 * P)',
    section: 'thickness_criteria',
  },
  thickness_criteria_min: {
    label: 't_{min} criteria',
    description: 'Minimum thickness criteria',
    formula: 'max(0.5 * t_{min}, t_{lim})',
    section: 'thickness_criteria',
  },
  effective_measured_thickness: {
    label: 't_{eff}',
    description: 'Effective measured thickness after corrosion allowance',
    formula: 't_{mm} - FCA_{ml}',
    section: 'thickness_criteria',
  },

  // ==================== Part 5: Flaw Size Criteria ====================
  rt_flaw: {
    label: 'R_t',
    description: 'Remaining thickness ratio',
    formula: '(t_{mm} - FCA_{ml}) / t^c',
    section: 'flaw_size_criteria',
  },
  tmm_threshold: {
    label: 't_{mm} - FCA_{ml}',
    description: 'Minimum thickness after future corrosion',
    formula: 't_{mm} - FCA_{ml}',
    section: 'flaw_size_criteria',
  },
  lmsd: {
    label: 'L_{msd}',
    description: 'Distance to nearest major structural discontinuity',
    formula: null,
    section: 'flaw_size_criteria',
  },
  lmsd_threshold: {
    label: 'L_{msd} threshold',
    description: 'Minimum distance to major structural discontinuity',
    formula: '1.8 * sqrt(D_{ml} * t^c)',
    section: 'flaw_size_criteria',
  },
  remainingThicknessThresholdRatio: {
    label: 'R_t threshold',
    description: 'Minimum remaining thickness ratio threshold',
    formula: null,
    section: 'flaw_size_criteria',
  },

  // ==================== Part 5: Final Acceptability Criteria ====================
  rsf: {
    label: 'RSF',
    description: 'Remaining strength factor',
    formula: 'R_t / (1 - (1/M_t) * (1 - R_t))',
    section: 'final_acceptability_criteria',
  },
  rsf_a_final: {
    label: 'RSF_a',
    description: 'Acceptable remaining strength factor',
    formula: null,
    section: 'final_acceptability_criteria',
  },
  mt: {
    label: 'M_t',
    description: 'Folias factor',
    formula: 'f(lambda)',
    section: 'final_acceptability_criteria',
  },
  rt_threshold: {
    label: 'R_t threshold',
    description: 'Remaining thickness ratio threshold for acceptability',
    formula: null,
    section: 'final_acceptability_criteria',
  },
}

/**
 * Get field definition by key, handling section-specific variations
 */
export function getFieldDefinition(
  key: string,
  section?: FfsFieldSection,
  context?: FfsFormulaContext
): FfsFieldDefinition | undefined {
  // Handle special cases where the same key exists in multiple sections
  if (key === 'rt' && section === 'flaw_size_criteria') {
    const base = FFS_FIELD_DEFINITIONS['rt_flaw']
    const override = getFormulaOverride('rt_flaw', section, context)
    return override !== undefined ? { ...base, formula: override } : base
  }
  if (key === 'rsf_a' && section === 'final_acceptability_criteria') {
    const base = FFS_FIELD_DEFINITIONS['rsf_a_final']
    const override = getFormulaOverride('rsf_a_final', section, context)
    return override !== undefined ? { ...base, formula: override } : base
  }
  const base = FFS_FIELD_DEFINITIONS[key]
  if (!base) return undefined
  const override = getFormulaOverride(key, section, context)
  return override !== undefined ? { ...base, formula: override } : base
}

/**
 * Get all field definitions for a specific section
 */
export function getFieldsForSection(
  section: FfsFieldSection
): Array<{ key: string; definition: FfsFieldDefinition }> {
  return Object.entries(FFS_FIELD_DEFINITIONS)
    .filter(([, def]) => def.section === section)
    .map(([key, definition]) => ({ key, definition }))
}

/**
 * Section display names for table titles
 */
export const SECTION_TITLES: Record<FfsFieldSection, string> = {
  user_params: 'User Parameters',
  data_values: 'Data Values',
  mawp: 'MAWP Calculations',
  calculated_values: 'Calculated Values',
  thickness_criteria: 'Thickness Criteria',
  flaw_size_criteria: 'Flaw Size Criteria',
  final_acceptability_criteria: 'Final Acceptability Criteria',
}

/**
 * Get the sections to display based on ffs_part
 */
export function getSectionsForPart(
  ffsPart: 'part-4' | 'part-5'
): FfsFieldSection[] {
  if (ffsPart === 'part-4') {
    return ['data_values', 'calculated_values', 'thickness_criteria', 'mawp']
  }
  return [
    'data_values',
    'flaw_size_criteria',
    'final_acceptability_criteria',
    'mawp',
  ]
}

/**
 * Field keys to display for each section (in display order)
 */
export const SECTION_FIELD_KEYS: Record<FfsFieldSection, string[]> = {
  user_params: ['fca', 'fcaMl'],
  data_values: [
    'tmm',
    'trd',
    'tc',
    'nominal',
    'longitudinalFlawLength',
    'latitudinalFlawLength',
    'innerDiameter',
    'outerDiameter',
    'lambda',
  ],
  mawp: ['mawp_design', 'mawp_c', 'mawp_l', 'mawpr', 'e', 'w', 's_y'],
  calculated_values: ['rt', 'rsf_a', 'q', 'l', 'ts_am', 'tc_am'],
  thickness_criteria: [
    't_lim',
    't_c_min',
    't_l_min',
    'thickness_criteria_min',
    'effective_measured_thickness',
  ],
  flaw_size_criteria: [
    'rt',
    'remainingThicknessThresholdRatio',
    'tmm_threshold',
    'lmsd',
    'lmsd_threshold',
  ],
  final_acceptability_criteria: ['rsf', 'rsf_a', 'mt', 'rt_threshold'],
}
